import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * A row's actions behind one trigger, instead of laid out in the row.
 *
 * WHY THIS EXISTS
 * The Entra users table put seven text buttons in its Actions cell. The other
 * six columns left that cell 94px wide, so the buttons wrapped into a vertical
 * stack and set EVERY row to 199px tall — five rows filled the viewport, and
 * the last column was pushed past the edge of its container and clipped. The
 * cell classes all looked reasonable; the height came entirely from wrapping.
 *
 * Measured before changing anything (`C:\Temp\shot\rowprobe.js`): row 199px,
 * actions cell 94px wide with seven children of 16/32/32/32/16/16/16.
 *
 * TWO THINGS THIS HAS TO GET RIGHT
 *
 * 1. It is rendered in a PORTAL, positioned from the trigger's viewport rect.
 *    These tables live inside `overflow-x-auto`, which establishes a clipping
 *    context: a normally-positioned dropdown gets cut off at the container
 *    edge, which is the very bug being fixed. Portalling is not decoration.
 *
 * 2. It flips up when there is no room below. A menu on the last row of a
 *    long table would otherwise open off-screen, and the actions on the row
 *    you most often act on would be the unreachable ones.
 */

export type RowAction = {
  label: string;
  onClick: () => void;
  /** Red styling. For anything that removes access or destroys data. */
  danger?: boolean;
  /** Greyed and unclickable — pass a reason so it isn't a mystery. */
  disabled?: boolean;
  /** Native tooltip; keep the explanation that was on the old inline button. */
  title?: string;
  hidden?: boolean;
};

const MENU_WIDTH = 208; // w-52
const GAP = 4;

export default function RowActions({
  actions,
  busy = false,
  label = "Actions",
}: {
  actions: RowAction[];
  /** An action is in flight for this row. */
  busy?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const shown = actions.filter((a) => !a.hidden);

  // Positioned after layout but before paint, so the menu never appears at the
  // wrong place for a frame.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const estimated = shown.length * 34 + 12;
    const below = window.innerHeight - r.bottom;
    const top = below < estimated + GAP ? r.top - estimated - GAP : r.bottom + GAP;
    // Right-aligned to the trigger, then pulled back inside the viewport
    // rather than allowed to hang off the left edge on a narrow window.
    const left = Math.max(GAP, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - GAP));
    setPos({ top: Math.max(GAP, top), left });
  }, [open, shown.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // Closed on scroll and resize rather than repositioned: the menu is
    // portalled, so it does not travel with the row it belongs to, and a menu
    // floating beside the wrong row is worse than one that closed.
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  if (shown.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border
                    border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400
                    hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700
                    dark:hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed
                    transition-colors ${open ? "bg-gray-100 dark:bg-gray-700" : ""}`}
      >
        {busy ? (
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.75" />
            <circle cx="12" cy="12" r="1.75" />
            <circle cx="12" cy="19" r="1.75" />
          </svg>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
            // z-[70] sits above the page and the z-50/z-[60] modal layers this
            // app already uses, and below the z-[100] top layer.
            className="fixed z-[70] py-1 rounded-xl border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-800 shadow-xl shadow-gray-900/10
                       dark:shadow-black/40 text-sm"
          >
            {shown.map((a) => (
              <button
                key={a.label}
                type="button"
                role="menuitem"
                title={a.title}
                disabled={a.disabled}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2
                            disabled:opacity-40 disabled:cursor-not-allowed
                            ${
                              a.danger
                                ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                                : "text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                            }`}
              >
                {a.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
